/**
 * Application form types — mirrors Flutter CandidateApplication models.
 *
 * Flutter source:
 *   lib/infrastructure/models/candidate_application/candidate_application_modal.dart
 *   lib/domain/core/constants/json_constants.dart
 */

/*
 * ── Field types — the `type` values Flutter BuildFormField (form_util.dart) renders, as in
 * FLD_TYPES plus the string cases of its switch. Any other type renders nothing. ──
 */
export enum FieldType {
  TEXT_FLD = 'TEXT_FLD',
  DESCRIPTION_TEXT_FLD = 'DESCRIPTION_TEXT_FLD',
  SEARCH_TEXT_FLD = 'SEARCH_TEXT_FLD',
  SEARCH_DROP_DOWN = 'SEARCH_DROP_DOWN',
  DROPDOWN_FOR_SINGLE_SELECTION = 'DROPDOWN_FOR_SINGLE_SELECTION',
  EXPERIENCE_DROP_DOWN = 'EXPERIENCE_DROP_DOWN',
  DATE = 'DATE',
  RADIO_BUTTON = 'RADIO_BUTTON',
  CHECKBOX = 'CHECKBOX',
  /** Layout spacer (Flutter BuildFormField "EMPTY") */
  EMPTY = 'EMPTY',
}

/* ── OptionData — dropdown options ── */
export interface OptionData {
  key: string;
  displayKey: string;
}

/* ── FldList — individual form field ── */
export interface FldListJson {
  key?: string;
  title: string;
  subTitle?: string;
  hintText?: string;
  isMandatory: boolean;
  type: string;
  value?: string;
  disableByKey?: string;
  data?: unknown[];
  optionsInYears?: string[];
  optionsInMonths?: string[];
  dropDownOptions?: Array<{ key: string; displayKey: string }>;
  isDisabled?: boolean;
  isMobileFld?: boolean;
  isEmailFld?: boolean;
  fromValue?: string;
  toValue?: string;
  selectedFromValue?: string;
  selectedToValue?: string;
  selectedValue?: string;
  digitsOnly?: boolean;
  masterDataKey?: string;
  isCurrentCompany?: boolean;
  disableCurrentCompany?: boolean;
  enableSearch?: boolean;
  isCurrencyDropDown?: boolean;
  currencySymbol?: string;
  currencyCode?: string;
  isInValid?: boolean;
  errorText?: string;
  subType?: string;
  hideOnLoading?: boolean;
  countryCode?: string;
}

export interface FldListState {
  key: string;
  title: string;
  subTitle: string;
  hintText: string;
  isMandatory: boolean;
  isMandatoryForLocal: boolean;
  type: string;
  value: string;
  displayValue: string;
  disableByKey: string;
  isDisabled: boolean;
  isMobileFld: boolean;
  isEmailFld: boolean;
  digitsOnly: boolean;
  masterDataKey: string;
  dropDownOptions: OptionData[];
  isCurrentCompany: boolean;
  disableCurrentCompany: boolean;
  enableSearch: boolean;
  isCurrencyDropDown: boolean;
  currencySymbol: string;
  currencyCode: string;
  isInValid: boolean;
  errorText: string;
  subType: string;
  countryCode: string;
  data?: unknown[];
}

/* ── Component — form section (personalInfo, address, education, etc.) ── */
export interface ComponentJson {
  headerTitle?: string;
  isHighestDegree?: boolean;
  fieldList?: FldListJson[];
}

export interface ComponentState {
  headerTitle: string;
  isHighestQualification: boolean;
  fields: FldListState[];
}

/* ── CandidateApplication — full form structure ── */
export interface CandidateApplicationJson {
  personalInformation: ComponentJson;
  address?: ComponentJson;
  education?: ComponentJson[];
  experience?: ComponentJson[];
  professionalDetails?: ComponentJson;
  professionalDetailsNonIndia?: ComponentJson;
  additionalInfo?: ComponentJson;
  questionInfo?: ComponentJson;
}

/* ── ProfileInput — passed as navigation state/argument ── */
export interface ProfileInput {
  clickedOn?: string;
  profileTabIndex?: number;
  tabIndex?: number;
  skills?: string;
  jobDisplayId?: string;
  jobTitle?: string;
  jobLocation?: string;
  jobId?: string;
  applicationId?: string;
  previousPage?: string;
}

/* ── Application submission payload ── */
export interface ApplicationSubmitPayload {
  jobsInfo: {
    id: string;
    displayId?: string;
    jobTitle?: string;
  };
  appliedStatus: string;
  personalInformation: Record<string, unknown>;
  education?: Record<string, unknown>[];
  experience?: Record<string, unknown>[];
  professionalDetails?: Record<string, unknown>;
  additionalInfo?: Record<string, unknown>;
  questionInfo?: Array<{ question: string; answer: string }>;
  page?: string;
  isRecommended?: boolean;
  channel?: string;
  sourceName?: string;
  applyType?: string;
}

/* ── Upload resume response ── */
export interface UploadResumeResponse {
  key?: string;
  [k: string]: unknown;
}

/* ── Master data response for lookupV2 ── */
export interface MasterDataOption {
  key: string;
  displayKey: string;
}

/* ── Resume extraction model — mirrors Flutter ExtractResumeModel ── */
export interface ExtractResumeModel {
  fileName: string;
  skillDetails?: Array<{ skill: string }>;
  personalDetails?: {
    location?: string;
    name?: string;
    email?: string;
    phone?: string;
  };
}

/* ── Supported file formats — mirrors Flutter OtherConstants.suppeortedFileFormats2 ── */
export const SUPPORTED_FILE_FORMATS = [
  'doc', 'docx', 'pdf', 'rtf', 'txt', 'ppt', 'pptx', 'odt',
];

export const SUPPORTED_FILE_MIMES: Record<string, string> = {
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
  rtf: 'application/rtf',
  txt: 'text/plain',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  odt: 'application/vnd.oasis.opendocument.text',
};

/** Max file size = 5MB — mirrors Flutter GenericHelpers.calculateTotalFilesSize check */
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

/** Accept string for file input */
export const FILE_ACCEPT_STRING = SUPPORTED_FILE_FORMATS.map(
  (ext) => `.${ext}`,
).join(',');

/* ── Click options — mirrors Flutter CLICK_OPTIONS enum ── */
export enum ClickOptions {
  APPLIED = 'APPLIED',
  EASY_APPLIED = 'EASY_APPLIED',
  SAVED = 'SAVED',
  INVITED = 'INVITED',
  RECOMMENDED = 'RECOMMENDED',
}

/* ── Application status values ── */
export enum ApplicationStatus {
  DRAFT = 'DRAFT',
  APPLIED = 'APPLIED',
  EASY_APPLIED = 'EASY_APPLIED',
}
